import { at } from "./assets.js";

// Beacon's Pack: the noises the device makes, and nothing else.
//
// Its own module rather than a second export from `pack.js`, for two reasons
// that are both about what else would evaluate it. Eleven Astro modules import
// `basePack` at marketing-build time — the marketing site should not walk a
// prop's manifest to render a sales page. And `cut-pack.js` refuses to rewrite
// a file holding more than one `const packVersion = …`, so two Packs in one
// file makes both of them uncuttable.
//
// It is a **prop**, not a Pack in the sense `vocabulary.md` defines — it owns no
// Set and no Games. It is a Pack in the sense that matters here: a versioned,
// immutable folder of media under an id, published and verified by the same
// three scripts. The word is stretched exactly that far and no further.
const packId = "beacon-v1";
// r1 (2026-09-06): the first cut. Six one-shots out of the hand-off in
//   `_src/games/beacon/sfx/`. Burnt — its URLs were requested before the upload.
// r2: burnt the same way.
// r3: the casing render, the looping `work` bed, and the plant and capture
//   takes. Burnt differently, and this is the one worth remembering: the casing
//   was re-cropped *in place* after r3 had been published, so the CDN went on
//   serving the first crop under a year of `immutable` while the bucket held the
//   second. The upload reports success; `verify:pack` catches it as a size
//   mismatch. New bytes need a new revision — that is the whole point of one.
// r4 (2026-09-07): the re-crop, plus `defused` and `detonated`. `detonated` was
//   added to r4 *after* it had been published, which is safe and is not the r3
//   mistake repeated: a key nobody has requested has nothing cached against it.
//   Changing the bytes under an existing key is the thing that needs a new
//   revision; adding a new key to a published one does not.
const packVersion = 4;
const local = at(packId, packVersion);

// Named for the moment, not for the recording — swapping the arming series is
// replacing `arm.mp3`, and nothing here has to be edited to do it.
//
// Six sounds serve both modes, because there is one device wearing two skins:
// a hold is a hold whether it is arming a charge or taking a terminal, and a
// second copy of the same beep under a different folder is one more thing to
// keep in step with a re-record.
//
// The beds are *built* rather than shipped. A 5-second arm, a 10-second defuse
// and a 5-second hack are three durations of one gesture, and shipping three
// beds means re-cutting all of them the day one number changes. `tick` played
// at an accelerating rate is the same escalation, it costs one file, and it is
// the escalating feedback the design asks for rather than a clip that has to
// finish.
//
// Every landing has its own take as of r4. `done` is no longer routed to and is
// kept as the fallback, so a state added tomorrow makes a noise rather than none.
export const beaconPack = {
  id: packId,
  version: packVersion,
  title: "Beacon",
  // The one picture in the Pack. Everything else Beacon draws is CSS, and the
  // terminal still is — a phosphor tube is light, and light is what CSS is good
  // at. A moulded plastic case under a strap is not: the gradients that stood in
  // for it were a drawing of an object, and this is one.
  //
  // Cropped to the device and no further, so the layout can position against its
  // own edges rather than against whatever margin a render happened to have.
  art: { casing: local("bomb", "casing.webp") },
  sfx: {
    // The hold's own pulse, played faster as the ring fills.
    tick: local("sfx", "tick.mp3"),
    // A keypad press, and a character landing in the scramble.
    key: local("sfx", "key.mp3"),
    // The attack a hold opens on: the device catching.
    arm: local("sfx", "arm.mp3"),
    // The bed under a hold, and the only clip here that loops. A hold is one
    // continuous process, so it is one sound held open and pitched up as the
    // charge takes — not a beep retriggered eleven times a second, which is
    // eleven decodes, eleven source nodes and eleven pokes at the phone's audio
    // session for something the ear hears as a single rising tone.
    work: local("sfx", "work.mp3"),
    // The long tone that says a hold has been taken and is being kept.
    hold: local("sfx", "hold.mp3"),
    // The run-up as a hold completes.
    rise: local("sfx", "rise.mp3"),
    // It landed: the generic confirm. Nothing routes to it any more — every
    // landing has its own take — and it stays as the fallback a new state gets
    // before somebody records one for it.
    done: local("sfx", "done.mp3"),
    // The moments with a take of their own, which is now all of them. Named for
    // the moment, not the recording — swapping the plant is replacing
    // `armed.mp3`, and nothing here has to be edited to do it.
    armed: local("sfx", "armed.mp3"),
    defused: local("sfx", "defused.mp3"),
    detonated: local("sfx", "detonated.mp3"),
    "secure-red": local("sfx", "secure-red.mp3"),
    "secure-blue": local("sfx", "secure-blue.mp3"),
  },
};

// Every asset the prop needs, so its gate has one list to fetch. The same shape
// as `assetsOf` in pack.js and deliberately not the same function: that one
// walks a Set of Games, and this one is six strings.
export const beaconAssets = () => [...Object.values(beaconPack.art), ...Object.values(beaconPack.sfx)];
